<script>
  import { asText } from '@prismicio/helpers'
  import { goto } from '$app/navigation'

  import { passengersToParams } from '$lib/utils/passengers.js'
  import TripSearchForm from '$lib/TripSearchForm.svelte'
  import PageHeading from '$lib/PageHeading.svelte'
  import { prefix } from '$lib/utils/prefix.js'
  import RichText from '$lib/RichText.svelte'
  import { resolve } from '$lib/prismic.js'
  import Footer from '$lib/Footer.svelte'
  import Slices from '$lib/Slices.svelte'
  import Header from '$lib/Header.svelte'
  import Button from '$lib/Button.svelte'
  import Figure from '$lib/Figure.svelte'
  import Meta from '$lib/Meta.svelte'
  import Hero from '$lib/Hero.svelte'

  let { data } = $props()

  let slices = $derived(
    data.page.data.body.filter(
      (item) =>
        item.slice_type !== 'journey_search_hero' && item.slice_type !== 'hero'
    )
  )

  let hero = $derived(
    data.page.data.body.find(
      (slice) =>
        slice.slice_type === 'hero' ||
        slice.slice_type === 'journey_search_hero'
    )
  )

  let languages = $derived(
    data.page.alternate_languages.length
      ? [data.page, ...data.page.alternate_languages]
          .map((doc) => {
            const href = resolve(doc)
            return href ? { href, lang: doc.lang } : null
          })
          .filter(Boolean)
      : []
  )

  const onsearch = ({ origin, destination, departureDate, passengers }) => {
    const params = new URLSearchParams({
      ddate: departureDate,
      origin: origin.uid,
      destination: destination.uid
    })

    for (const value of passengersToParams(passengers)) {
      params.append('passenger', value)
    }

    goto(`${$prefix}/search?${params}`)
  }
</script>

<Meta
  title={[asText(data.page.data.title), data.settings?.data.title]
    .filter(Boolean)
    .join(' – ')}
  description={data.page.data.description}
  image={data.page.data.featured_image?.url
    ? data.page.data.featured_image
    : hero?.primary?.image}
  robots={data.page.data.private ? 'noindex, nofollow' : null} />

<Header
  {languages}
  style={data.page.data.body[0]?.primary?.gray
    ? '--current-background: rgb(var(--rgb-bright-gray))'
    : ''}
  adaptive={Boolean(hero)}
  currencies={data.page.data.body.some(
    (slice) => slice.slice_type === 'interrail_shop'
  )}>
</Header>

{#if hero?.slice_type === 'journey_search_hero'}
  <Hero window>
    <Figure fill slot="background" image={hero.primary.image} />
    <section class="layout">
      <div class="body">
        <div class="heading">
          <RichText content={hero.primary.heading} />
        </div>
        <div class="text">
          <RichText content={hero.primary.description} />
        </div>
      </div>
      <div class="children">
        <div class="search-form">
          <TripSearchForm
            {onsearch}
            geolocate
            origin={data.origin}
            destination={data.destination} />
        </div>
      </div>
    </section>
  </Hero>
{/if}

{#if hero?.slice_type === 'hero'}
  <Hero>
    <Figure slot="background" image={hero.primary.image} fill loading="eager" />
    <PageHeading title={asText(hero.primary.heading)}>
      <RichText slot="tagline" content={hero.primary.tagline} />
      <RichText slot="body" content={hero.primary.preamble} />
      <svelte:fragment slot="content">
        {#each hero.items.filter((item) => item.button_text) as item}
          {@const { button_hook: hook, button_text: children } = item}
          {@const href = resolve(item.button_link)}
          {#if hook || href}
            <Button
              size="lg"
              href={href || `#${hook}`}
              onclick={(event) => {
                const el = document.getElementById(hook)
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  event.preventDefault()
                }
              }}>
              {children}
            </Button>
          {/if}
        {/each}
      </svelte:fragment>
    </PageHeading>
  </Hero>
{/if}

<div class="page">
  <Slices {slices} />
</div>

<Footer newsletter />

<style>
  .layout {
    display: flex;
    height: 100%;
    width: 100%;
    max-width: var(--document-width);
    flex-direction: column;
    justify-content: space-between;
    margin: auto;
    color: rgb(var(--rgb-text));
  }

  @media (min-width: 900px) {
    .layout {
      flex-direction: row;
      align-items: center;
    }
  }

  .body {
    flex: 1 1 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    position: relative;
    margin: 0 auto 2rem;
    padding: 0 0.5rem;
    width: 100%;
    text-align: center;
    color: rgb(var(--rgb-white));
  }

  @media (min-width: 500px) {
    .body {
      margin: 0 auto 1.5rem;
    }
  }

  @media (min-width: 900px) {
    .body {
      display: block;
      position: relative;
      margin: 0;
      padding: 0 4rem 0 0;
      text-align: left;
    }
  }

  .children {
    width: 100%;
    position: relative;
  }

  @media (min-width: 900px) {
    .children {
      max-width: 35rem;
      min-width: 27rem;
    }

    .search-form {
      width: 100%;
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
    }
  }

  .heading {
    font-family: var(--heading-font-family);
    font-weight: bold;
    font-size: clamp(4.75rem, 25vw, 7rem);
    line-height: 0.8;
    letter-spacing: -0.02em;
    word-spacing: -0.05em;
    white-space: nowrap;
  }

  :global(:root[lang='sv']) .heading {
    line-height: 0.8;
  }

  @media (min-width: 1000px) {
    .heading {
      font-size: 6rem;
    }
  }

  @media (min-width: 900px) {
    .heading {
      margin-left: -0.02em;
      font-size: clamp(4.25rem, 11vw, 9.5rem);
    }
  }

  @media (min-width: 1200px) {
    .heading {
      font-size: 8rem;
    }
  }

  .text {
    margin-top: 1rem;
    max-width: 20em;
    font-size: 1.25rem;
    line-height: 1.2;
  }
</style>
